import { Controller } from "@hotwired/stimulus"

// Scales the natural-width resume document inside the thumbnail iframe down to
// the card's width, cropping the top of the page. The document renders at a
// fixed 720px width (matching the .rb-doc max-width) so scaling preserves the
// real layout instead of reflowing it.
//
// Observes the BOX, not the window. On the dashboard every document panel ships
// with `class="hidden"` and dashboard-docs-tabs reveals one in its own connect().
// Two independent Stimulus controllers have no guaranteed connect order, and even
// in the lucky order a connect-time read can land before layout — so clientWidth
// was 0, scale() bailed on its own `if (!w || !h)` guard, and the iframe rendered
// at its natural 720px inside a ~200px card. The window-resize listener could not
// recover it, because switching tabs is not a window resize. A ResizeObserver
// fires the moment the box first gains a size, covering the hidden-at-connect
// case, tab switches and window resizes in one mechanism.
export default class extends Controller {
  static targets = ["frame"]

  connect() {
    this.naturalWidth = 720
    this.scale = this.scale.bind(this)
    this.scale() // fast path for a box that is already laid out

    if (typeof ResizeObserver === "function") {
      this.observer = new ResizeObserver(this.scale)
      this.observer.observe(this.element)
    } else {
      // No ResizeObserver: keep the old behaviour rather than nothing at all.
      window.addEventListener("resize", this.scale)
    }
  }

  disconnect() {
    if (this.observer) {
      this.observer.disconnect()
      this.observer = null
    } else {
      window.removeEventListener("resize", this.scale)
    }
  }

  scale() {
    if (!this.hasFrameTarget) return
    const box = this.element
    const frame = this.frameTarget
    const w = box.clientWidth
    const h = box.clientHeight
    if (!w || !h) return

    const k = w / this.naturalWidth
    // Make the iframe tall enough that, once scaled by k, it covers the crop box.
    const frameHeight = Math.ceil(h / k)

    // Write only on change. The frame is a child of the observed box, so an
    // unconditional write is a plausible ResizeObserver feedback loop. The box is
    // fixed-height with overflow hidden today, so it cannot loop — this keeps
    // that safe if the box ever becomes content-sized.
    if (k === this.lastK && frameHeight === this.lastFrameHeight) return
    this.lastK = k
    this.lastFrameHeight = frameHeight

    frame.style.height = `${frameHeight}px`
    frame.style.transform = `scale(${k})`
  }
}
